import { coerceDate } from "../../../lib/time";

// Pure helpers that decide whether the Create / Voting calendar should
// auto-navigate to the month of a poll's earliest slot.
//
// THE BUG (audit A-2): `calendarDate` initializes to today. When every slot of
// a poll falls in a FUTURE month, the RBC grid opens on an empty current month
// and the month-scoped CreatorSlotAgenda shows a false "No slots proposed yet."
//
// THE BASIS: callers pass slot start Dates ALREADY shifted into the display
// timezone (via toDisplayDate) — the exact local-field basis the agenda and the
// RBC grid compare against `calendarDate` (getFullYear()/getMonth()). Keeping
// the comparison on that basis here means these functions are timezone-free and
// can be unit-tested with plain local Dates.

function validDates(displayStarts) {
  if (!Array.isArray(displayStarts)) return [];
  return displayStarts.filter(
    (d) => d instanceof Date && !Number.isNaN(d.getTime())
  );
}

/**
 * The chronologically earliest valid Date in `displayStarts`, or null when
 * there are no valid entries. Used by the empty-month CTA to point at (and
 * jump to) the soonest slot.
 */
export function earliestDisplayStart(displayStarts) {
  const valid = validDates(displayStarts);
  if (valid.length === 0) return null;
  return valid.reduce(
    (earliest, d) => (d.getTime() < earliest.getTime() ? d : earliest),
    valid[0]
  );
}

/**
 * The Date the calendar should navigate to on initial slot load, or null to
 * leave the displayed month unchanged.
 *
 * Returns null when there are no (valid) slots, or when at least one slot
 * already falls in the displayed month (a slot is visible — don't yank the
 * view away from the user). Otherwise returns the earliest slot so the grid /
 * agenda opens where the slots actually are.
 */
export function resolveInitialCalendarMonth(displayStarts, currentDate) {
  const valid = validDates(displayStarts);
  if (valid.length === 0) return null;
  const current = coerceDate(currentDate);
  if (current) {
    const curYear = current.getFullYear();
    const curMonth = current.getMonth();
    const anyInCurrentMonth = valid.some(
      (d) => d.getFullYear() === curYear && d.getMonth() === curMonth
    );
    if (anyInCurrentMonth) return null;
  }
  return earliestDisplayStart(valid);
}
